import React, { forwardRef, Fragment, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { m as motion } from 'framer-motion';
import { SectionInner } from '@polestar/component-warehouse-react/components/section/Section';
import { LoadMediaInView } from '@polestar/component-warehouse-react/components/in-view/InView';
import { GalleryContext } from '@polestar/component-warehouse-react/components/gallery/GalleryContext';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import AspectRatio from '@polestar/component-warehouse-react/components/aspect-ratio/AspectRatio';
import { MotionControlsVariants } from '@polestar/component-warehouse-react/components/motion/useMotionControls';
import { MotionDurationsSeconds } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useFocusGroup } from '@polestar/component-warehouse-react/hooks/focus';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useGalleryControls, } from '@polestar/component-warehouse-react/components/gallery/useGalleryControls';
import { useFocusProvider } from '@polestar/component-warehouse-react/contexts/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import GalleryControlled from './internal/GalleryControlled';
import { galleryMaximumContentLength } from './internal/GalleryIndicator';
import GallerySingleItem from './internal/GallerySingleItem';
import TextIndicator from './internal/GalleryTextIndicator';
const EMPTY_ARRAY = [];
export var GalleryVariants;
(function (GalleryVariants) {
    GalleryVariants["standard"] = "standard";
    GalleryVariants["contained"] = "contained";
    GalleryVariants["highContrastChevrons"] = "highContrastChevrons";
})(GalleryVariants || (GalleryVariants = {}));
export var GallerySlideTypes;
(function (GallerySlideTypes) {
    GallerySlideTypes["image"] = "image";
    GallerySlideTypes["video"] = "video";
})(GallerySlideTypes || (GallerySlideTypes = {}));
const containerVariants = {
    [MotionControlsVariants.visible]: {
        transition: {
            staggerChildren: MotionDurationsSeconds.normal,
        },
    },
};
const Gallery = forwardRef(({ onChange, content = EMPTY_ARRAY, inBreakoutSection = false, variant = GalleryVariants.standard, hideIndicator: _hideIndicator, controls, aspectRatio, dragThreshold, arrowLeftButtonProps, arrowRightButtonProps, ariaLabels, ...props }, ref) => {
    var _a;
    if (variant !== GalleryVariants.highContrastChevrons &&
        content.length > galleryMaximumContentLength) {
        content.splice(galleryMaximumContentLength);
        // eslint-disable-next-line no-console
        console.error(`Gallery: please follow the Polestar Guidelines > Gallery and do not provide more than ${galleryMaximumContentLength} items in the "Gallery.content" property! If you wish to use more than ${galleryMaximumContentLength} items, then please use the "GalleryVariants.highContrastChevrons" variant instead.`);
    }
    /* TODO: When/if these features are removed, remove the following code and refactor */
    /* Set these as `const` until we decide if these features should be permanently removed */
    const CustomComponent = undefined;
    /* End */
    const hideIndicator = _hideIndicator;
    const _controls = useGalleryControls({
        contentLength: content.length,
    });
    const { index: clampedIndex, internalIndex, setIndex } = controls || _controls;
    const contentLength = content.length;
    const memoizedId = useSSRSafeId('gallery', props.id);
    const captions = useMemo(() => content
        .map((item) => item === null || item === void 0 ? void 0 : item.caption)
        .filter((val) => val !== undefined), [content]);
    const SectionWrapper = inBreakoutSection ? SectionInner : React.Fragment;
    const isInView = useMutableValue(false);
    const AspectWrapper = aspectRatio ? AspectRatio : Fragment;
    const aspectWrapperProps = aspectRatio ? { ratio: aspectRatio } : {};
    const showCaption = ((content === null || content === void 0 ? void 0 : content.length) > 1 || !!((_a = content === null || content === void 0 ? void 0 : content[0]) === null || _a === void 0 ? void 0 : _a.caption)) && !hideIndicator;
    if (content.length === 0) {
        return null;
    }
    if (content.length === 1) {
        return (React.createElement(GallerySingleItem, { ref: ref, content: content[0], variant: variant, aspectRatio: aspectRatio, inBreakoutSection: inBreakoutSection, captions: captions, showCaption: showCaption, clampedIndex: clampedIndex }));
    }
    return (React.createElement(LoadMediaInView, { offset: "0px 0px -60% 0px", triggerOnce: true },
        React.createElement(Container, { ref: ref, sizeToParent: true, variant: variant, variants: containerVariants, ...props, "aria-live": "polite" },
            React.createElement(AspectWrapper, { ...aspectWrapperProps },
                React.createElement(GalleryContext.Provider, { value: {
                        id: memoizedId,
                        inGallery: true,
                        index: clampedIndex,
                        internalIndex,
                        isInView,
                        setIndex,
                    } },
                    React.createElement(GalleryControlled, { onChange: onChange, content: content, internalIndex: internalIndex, clampedIndex: clampedIndex, CustomComponent: CustomComponent, variant: variant, dragThreshold: dragThreshold, arrowLeftButtonProps: arrowLeftButtonProps, arrowRightButtonProps: arrowRightButtonProps, ariaLabels: ariaLabels }))),
            (content === null || content === void 0 ? void 0 : content.length) > 1 && variant !== GalleryVariants.standard && !hideIndicator && (React.createElement(A11yHelper, { clampedIndex: clampedIndex, internalIndex: internalIndex, length: contentLength, memoizedId: memoizedId })),
            showCaption && (React.createElement(SectionWrapper, null,
                React.createElement(TextIndicator, { clampedIndex: clampedIndex, length: contentLength, captions: captions, "data-type": "indicator", variant: variant }))))));
});
Gallery.displayName = 'Gallery';
export default Gallery;
const A11yHelper = ({ length, clampedIndex, internalIndex, memoizedId, ...props }) => {
    const internalRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    useEffect(() => clampedIndex.on('change', (latestActiveIndex) => {
        setActiveIndex(latestActiveIndex);
    }), [clampedIndex]);
    const handleFocus = useCallback((e) => {
        const index = parseInt(e.target.dataset.index);
        internalIndex.set(index);
    }, [internalIndex]);
    const { setFocusIndex, createFocusProps } = useFocusGroup({
        ref: internalRef,
        initialFocusIndex: activeIndex,
        orientation: Orientations.horizontal,
        onFocus: handleFocus,
    });
    useEffect(() => {
        setFocusIndex(activeIndex);
    }, [activeIndex, setFocusIndex]);
    const { focusParentProps, focusProps } = useFocusProvider({
        id: memoizedId,
    });
    return (React.createElement(AriaHelper, { ref: internalRef, ...aria({
            role: 'tablist',
        }), ...props, ...focusParentProps }, Array(length)
        .fill(null)
        .map((item, index) => (React.createElement("button", { key: index, type: "button", ...aria({
            role: 'tab',
            selected: activeIndex === index,
            controls: `${memoizedId}-${index}`,
        }), onClick: handleFocus, ...createFocusProps(index), ...focusProps }, index + 1)))));
};
const AriaHelper = styled.div `
  position: absolute;
  pointer-events: none;
  height: 100%;
  width: 100%;

  * {
    position: absolute;
    inset-block-end: 0;
    inset-inline-start: 0;
    opacity: 0;
  }
`;
const Container = styled(motion.div, shouldNotForwardProps(false, 'sizeToParent'))(({ sizeToParent, variant }) => css(css `
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 0;
      flex: 0;
      touch-action: pan-y;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
    `, sizeToParent &&
    css `
        width: 100%;
        height: 100%;
      `, variant === GalleryVariants.contained &&
    css `
        width: auto;
        max-height: calc(100vw - 3%);
      `));
